import React, { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import { HeaderPortal, useSetHeaderRecordName } from "../../components/ui-components/HeaderSlot.jsx";
import { primaryButtonCls, secondaryButtonCls } from "../../components/ui-components/buttonStyles.js";
import FormButton from "../../components/form-components/FormButton";
import Modal from "../../components/ui-components/Modal";
import { Dropdown } from "../../components/form-components/Dropdown";
import Skeleton from "../../components/ui-components/Skeleton";
import StatusPill from "../Budgets/components/StatusPill";
import { toast } from "../../components/ui-components/Toast";

import {
  useApprovalPolicyQuery,
  useCreateApprovalPolicyMutation,
  useUpdateApprovalPolicyMutation,
  useDisableApprovalPolicyMutation,
} from "./hooks/useApprovalsQueries";

import {
  createDefaultPolicy,
  createDefaultTier,
  parsePolicyDefinition,
  serializeToPayload,
  mergeDefinitionForSave,
  validatePolicy,
  tryMigrateLegacy,
  sortTiersByMinAmount,
} from "./policyBuilderState";

import TierListPanel from "./components/TierListPanel";
import TierWorkspacePanel from "./components/TierWorkspacePanel";
import PolicyPreviewSimulator from "./components/PolicyPreviewSimulator";
import RightSheet from "../../components/ui-components/RightSheet";
import { derivePolicyHealth } from "./derivePolicyHealth";

const OBJECT_TYPE_OPTIONS = [
  { value: "budget_period_version", label: "Budget period version" },
  { value: "budget_creation", label: "Budget creation" },
];

const BUDGET_TYPE_OPTIONS = [
  { value: "", label: "Any" },
  { value: "operational", label: "Operational" },
  { value: "capital", label: "Capital" },
];

const CURRENCY_OPTIONS = [
  { value: "EUR", label: "EUR" },
  { value: "USD", label: "USD" },
  { value: "GBP", label: "GBP" },
];

const ApprovalPolicyEditorPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const { data: loadedPolicy, isLoading, error } = useApprovalPolicyQuery(id);

  const [metadata, setMetadata] = useState(null);
  const [definition, setDefinition] = useState(null);
  const [selectedTierId, setSelectedTierId] = useState(null);
  const [newlyCreatedTierId, setNewlyCreatedTierId] = useState(null);
  const [isLegacy, setIsLegacy] = useState(false);
  const [legacyJson, setLegacyJson] = useState(null);
  const [originalPolicyJson, setOriginalPolicyJson] = useState(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [isSimulatorOpen, setSimulatorOpen] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [touched, setTouched] = useState({ name: false });

  const createMutation = useCreateApprovalPolicyMutation();
  const updateMutation = useUpdateApprovalPolicyMutation();
  const disableMutation = useDisableApprovalPolicyMutation();

  useEffect(() => {
    if (isEditMode) {
      if (!loadedPolicy) return;
      const policyMeta = {
        name: loadedPolicy.name || "",
        object_type: loadedPolicy.object_type || "",
        budget_type: "",
        priority: 100,
        status: loadedPolicy.is_enabled === false ? "disabled" : "enabled",
        currency: "EUR",
      };
      setIsDisabled(loadedPolicy.is_enabled === false);
      setOriginalPolicyJson(structuredClone(loadedPolicy.policy_json));
      const parsed = parsePolicyDefinition(loadedPolicy.policy_json);
        if (parsed.ok) {
        if (parsed.metadata) {
          policyMeta.budget_type = parsed.metadata.budget_type || "";
          policyMeta.priority = parsed.metadata.priority ?? 100;
          policyMeta.currency = parsed.metadata.currency || "EUR";
        }
        setMetadata(policyMeta);
        setDefinition({
          ...parsed.definition,
          tiers: sortTiersByMinAmount(parsed.definition.tiers),
        });
        setSelectedTierId(parsed.definition.tiers[0]?.id || null);
        setIsLegacy(false);
      } else {
        setMetadata(policyMeta);
        setIsLegacy(true);
        setLegacyJson(loadedPolicy.policy_json);
      }
      setInitialized(true);
    } else {
      const prefillType = searchParams.get("object_type") || "";
      const defaults = createDefaultPolicy(prefillType);
      setMetadata(defaults.metadata);
      setDefinition({
        ...defaults.definition,
        tiers: sortTiersByMinAmount(defaults.definition.tiers),
      });
      setSelectedTierId(defaults.definition.tiers[0]?.id || null);
      setIsLegacy(false);
      setIsDisabled(false);
      setInitialized(true);
    }
  }, [isEditMode, loadedPolicy, searchParams]);

  const validation = useMemo(() => {
    if (!metadata || !definition) return { valid: false, errors: [], warnings: [] };
    return validatePolicy(metadata, definition, !isEditMode);
  }, [metadata, definition, isEditMode]);

  const blockingIssues = validation.errors;
  const showGlobalBanner = submitAttempted && blockingIssues.length > 0 && !isLegacy;
  const nameIsEmpty = !metadata?.name?.trim();
  const showNameInlineError = (touched.name || submitAttempted) && nameIsEmpty;

  function getIssueAnchor(issue) {
    if (issue.field === "name" || issue.field === "object_type") return "#policy-name";
    if (issue.field === "tiers") return "#tiers";
    if (issue.field === "chain" || issue.field === "tier") return "#approval-chain";
    return "#policy-name";
  }

  function handleIssueLinkClick(e, anchor) {
    e.preventDefault();
    const el = document.querySelector(anchor);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    if (anchor === "#policy-name") {
      const input = document.getElementById("policy-name-input");
      input?.focus();
    }
  }

  const tiers = useMemo(() => definition?.tiers || [], [definition]);
  const selectedTier = tiers.find((t) => t.id === selectedTierId) || null;
  const policyHealth = useMemo(
    () => (!isLegacy && validation ? derivePolicyHealth(validation, tiers) : null),
    [isLegacy, validation, tiers]
  );

  const firstTierId = tiers[0]?.id ?? null;

  // Auto-select first tier when tiers exist and none selected (avoids dead-end state)
  useEffect(() => {
    if (tiers.length > 0 && !selectedTierId && firstTierId) {
      setSelectedTierId(firstTierId);
    }
  }, [tiers.length, selectedTierId, firstTierId]);

  const handleTiersChange = (newTiers) => {
    const sorted = sortTiersByMinAmount(newTiers);
    setDefinition((d) => ({ ...d, tiers: sorted }));
    if (newTiers.length === 0) {
      setSelectedTierId(null);
    } else if (!sorted.find((t) => t.id === selectedTierId)) {
      setSelectedTierId(sorted[0].id);
    }
  };

  const handleAddTier = () => {
    const newTier = createDefaultTier();
    const newTiers = [...tiers, newTier];
    handleTiersChange(newTiers);
    setSelectedTierId(newTier.id);
    setNewlyCreatedTierId(newTier.id);
  };

  // Clear newlyCreatedTierId after one render (for auto-focus)
  useEffect(() => {
    if (newlyCreatedTierId) {
      const t = setTimeout(() => setNewlyCreatedTierId(null), 0);
      return () => clearTimeout(t);
    }
  }, [newlyCreatedTierId]);

  const handleTierChange = (updatedTier) => {
    setDefinition((d) => {
      const updated = d.tiers.map((t) => (t.id === updatedTier.id ? updatedTier : t));
      return { ...d, tiers: sortTiersByMinAmount(updated) };
    });
  };

  const handleSave = async () => {
    setSubmitAttempted(true);
    setTouched((t) => ({ ...t, name: true }));
    if (blockingIssues.length > 0) {
      setTimeout(() => {
        document.getElementById("validation-banner")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
      return;
    }
    try {
      const payload = serializeToPayload(metadata, definition);
      if (isEditMode) {
        const mergedJson = mergeDefinitionForSave(originalPolicyJson, payload.policy_json);
        const updatePayload = { ...payload, policy_json: mergedJson };
        await updateMutation.mutateAsync({ id, payload: updatePayload });
        toast.success("Policy updated");
      } else {
        await createMutation.mutateAsync(payload);
        toast.success("Policy created");
      }
      navigate("/control-policies/approval-policies");
    } catch (e) {
      // handled by mutation
    }
  };

  const handleDisable = async () => {
    try {
      await disableMutation.mutateAsync(id);
      toast.success("Policy disabled");
      setIsDisabled(true);
      setMetadata((m) => ({ ...m, status: "disabled" }));
      setDisableModalOpen(false);
    } catch (e) {
      // handled by mutation
    }
  };

  const handleMigrateLegacy = () => {
    const result = tryMigrateLegacy(legacyJson);
    if (result.ok) {
      setDefinition(result.definition);
      setSelectedTierId(result.definition.tiers[0]?.id || null);
      setIsLegacy(false);
      toast.success("Migrated to builder format");
    } else {
      toast.error("Cannot auto-migrate this format.");
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const headerTitle = isEditMode
    ? metadata?.name || "..."
    : "Create policy";

  useSetHeaderRecordName(isEditMode ? (metadata?.name || null) : null);

  const statusKey = isDisabled ? "archived" : "active";

  // -- Loading state ----------------------------------------------------------
  if (isEditMode && isLoading) {
    return (
      <div className="p-8">
        <Skeleton height={400} />
      </div>
    );
  }

  // -- Error state ------------------------------------------------------------
  if (isEditMode && error) {
    return (
      <div className="h-full flex flex-col bg-background-grey">
        <div className="sticky top-0 z-40 border-b border-border flex items-center h-[64px] pl-8 pr-4">
          <span className="text-lg text-text font-semibold font-['Plus_Jakarta_Sans',sans-serif]">
            Approval policy
          </span>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-danger mb-2">Error loading policy</div>
            <FormButton
              title="Back to list"
              style="secondary"
              onClick={() => navigate("/control-policies/approval-policies")}
            />
          </div>
        </div>
      </div>
    );
  }

  if (!initialized || !metadata) return null;

  // Header action buttons — shared governance styles (filled-slate primary + neutral
  // secondary), matching /approvals/:id. See buttonStyles.js.
  const primaryBtnCls = primaryButtonCls;
  const secondaryBtnCls = secondaryButtonCls;

  return (
    <div className="h-full flex flex-col bg-background-grey relative">
      {/* Action buttons live in the global top bar (next to the tab switcher), like /approvals/:id. */}
      <HeaderPortal>
        <div className="flex items-center gap-2.5">
          {!isDisabled && !isLegacy && (
            <button type="button" className={secondaryBtnCls} onClick={() => setSimulatorOpen(true)}>
              Preview &amp; simulate
            </button>
          )}
          <button
            type="button"
            className={secondaryBtnCls}
            onClick={() => navigate("/control-policies/approval-policies")}
          >
            Cancel
          </button>
          {isEditMode && !isDisabled && (
            <button type="button" className={secondaryBtnCls} onClick={() => setDisableModalOpen(true)}>
              Disable
            </button>
          )}
          {!isDisabled && !isLegacy && (
            <button type="button" className={primaryBtnCls} onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving..." : isEditMode ? "Save" : "Create"}
            </button>
          )}
        </div>
      </HeaderPortal>

      {/* Title + status live in the header's row-1 title slot. */}
      <HeaderPortal slot="title">
        <div className="flex items-center gap-3 min-w-0">
          <div className="text-[20px] leading-[1.4] font-extrabold tracking-[-0.015em] text-text truncate font-['Plus_Jakarta_Sans',sans-serif]">
            {headerTitle}
          </div>
          {isEditMode && <StatusPill status={statusKey} />}
        </div>
      </HeaderPortal>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-auto p-6">
        {/* Disabled banner */}
        {isDisabled && (
          <div className="mb-4 px-4 py-3 bg-surface-2 border border-border rounded-card text-sm text-text-2">
            This policy is disabled and read-only.
          </div>
        )}

        {/* Metadata card */}
        <div className="bg-card border border-card-bd rounded-card p-5 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div id="policy-name" className="min-w-[240px] flex-1 max-w-[360px]">
              <label className="block text-sm font-medium text-text-2 mb-1">Policy name</label>
              <input
                id="policy-name-input"
                type="text"
                className={`w-full p-2 border rounded-ctrl text-sm ${showNameInlineError ? "border-danger" : "border-border"}`}
                placeholder="e.g. Budget activation approval"
                value={metadata.name}
                onChange={(e) => setMetadata((m) => ({ ...m, name: e.target.value }))}
                onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                disabled={isDisabled}
              />
              {showNameInlineError && (
                <p className="mt-1 text-xs text-danger">Policy name is required.</p>
              )}
            </div>
            <div className="min-w-[180px]">
              <label className="block text-sm font-medium text-text-2 mb-1">Object type</label>
              <Dropdown
                options={OBJECT_TYPE_OPTIONS}
                selectedValue={OBJECT_TYPE_OPTIONS.find((o) => o.value === metadata.object_type) || OBJECT_TYPE_OPTIONS[0]}
                setSelectedValue={(opt) => setMetadata((m) => ({ ...m, object_type: opt.value }))}
                fullWidth
                disabled={isEditMode || isDisabled}
              />
            </div>
            <div className="min-w-[140px]">
              <label className="block text-sm font-medium text-text-2 mb-1">Budget type</label>
              <Dropdown
                options={BUDGET_TYPE_OPTIONS}
                selectedValue={BUDGET_TYPE_OPTIONS.find((o) => o.value === metadata.budget_type) || BUDGET_TYPE_OPTIONS[0]}
                setSelectedValue={(opt) => setMetadata((m) => ({ ...m, budget_type: opt.value }))}
                fullWidth
                disabled={isDisabled}
              />
            </div>
            <div className="min-w-[100px]">
              <label className="block text-sm font-medium text-text-2 mb-1">Currency</label>
              <Dropdown
                options={CURRENCY_OPTIONS}
                selectedValue={CURRENCY_OPTIONS.find((o) => o.value === (metadata.currency || "EUR")) || CURRENCY_OPTIONS[0]}
                setSelectedValue={(opt) => opt && setMetadata((m) => ({ ...m, currency: opt.value }))}
                fullWidth
                disabled={isDisabled}
              />
            </div>
            <div className="w-[100px]">
              <label className="block text-sm font-medium text-text-2 mb-1">Priority</label>
              <input
                type="number"
                className="w-full p-2 border border-border rounded-ctrl text-sm"
                value={metadata.priority}
                onChange={(e) => setMetadata((m) => ({ ...m, priority: Number.parseInt(e.target.value, 10) || 0 }))}
                disabled={isDisabled}
              />
            </div>
          </div>
        </div>

        {/* Consolidated validation banner (only after Save/Enable attempt) */}
        {showGlobalBanner && (
          <div id="validation-banner" className="mb-4 px-4 py-3 bg-danger-soft border border-danger rounded-card text-sm text-danger">
            <div className="font-medium mb-1">Can&apos;t save yet</div>
            <p className="text-danger mb-2">Resolve the following {blockingIssues.length} issue(s):</p>
            <ul className="list-disc list-inside space-y-1">
              {blockingIssues.map((issue, i) => (
                <li key={i}>
                  <a
                    href={getIssueAnchor(issue)}
                    onClick={(e) => handleIssueLinkClick(e, getIssueAnchor(issue))}
                    className="text-danger hover:opacity-80 underline cursor-pointer"
                  >
                    {issue.message}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Legacy fallback */}
        {isLegacy ? (
          <div className="bg-card border border-card-bd rounded-card p-6">
            <div className="mb-4 px-4 py-3 bg-warning-soft border border-warning rounded-card text-sm text-warning">
              This policy uses a legacy format. Edit in JSON mode.
            </div>
            <pre className="bg-surface-2 p-4 rounded-ctrl text-xs font-mono overflow-auto max-h-[400px] text-text">
              {JSON.stringify(legacyJson, null, 2)}
            </pre>
            <div className="mt-4">
              <FormButton
                title="Migrate to builder"
                style="secondary"
                onClick={handleMigrateLegacy}
                disabled={isDisabled || !tryMigrateLegacy(legacyJson).ok}
              />
            </div>
          </div>
        ) : (
          /* Builder layout: 2-col grid (Tier List | Tier Editor + Chain) */
          <div className="grid gap-6 md:grid-cols-[360px_minmax(0,1fr)] flex-1 min-w-0">
            <div id="tiers">
              <TierListPanel
                tiers={tiers}
                selectedTierId={selectedTierId}
                onSelectTier={setSelectedTierId}
                onTiersChange={handleTiersChange}
                onAddTier={handleAddTier}
                currency={metadata.currency || "EUR"}
                disabled={isDisabled}
                errors={submitAttempted ? validation.errors : []}
                policyHealth={policyHealth}
              />
            </div>
            <div id="tier-workspace">
            <TierWorkspacePanel
              tier={selectedTier}
              onTierChange={handleTierChange}
              onAddTier={handleAddTier}
              objectType={metadata.object_type}
              currency={metadata.currency || "EUR"}
              disabled={isDisabled}
              errors={[]}
              warnings={[]}
              autoFocusName={selectedTierId === newlyCreatedTierId}
            />
            </div>
          </div>
        )}
      </div>

      <RightSheet
        open={isSimulatorOpen}
        onClose={() => setSimulatorOpen(false)}
        title="Preview & simulate"
        subtitle="Test scenarios to see which tier matches and which approvals are required."
      >
        <div className="space-y-4">
          {policyHealth?.status === "warn" && (
            <div className="px-4 py-3 bg-warning-soft border border-warning rounded-card text-sm text-warning">
              Policy has validation issues. Simulation may be unreliable until resolved.
            </div>
          )}
          {isSimulatorOpen && (
            <PolicyPreviewSimulator
              tiers={tiers}
              objectType={metadata.object_type}
              currency={metadata.currency || "EUR"}
            />
          )}
        </div>
      </RightSheet>

      <Modal
        show={disableModalOpen}
        close={() => setDisableModalOpen(false)}
        title="Disable policy?"
        description="This policy will no longer require approval for new requests. This cannot be undone."
        confirmButton="Disable"
        cancelButton="Cancel"
        alertIcon
        confirmAction={handleDisable}
        icon={icon({ name: "circle-xmark", style: "solid" })}
      />
    </div>
  );
};

export default ApprovalPolicyEditorPage;
